import {
  ProjectsFilterOptionService,
  DevicesFilterOptionService,
  UserFilterOptionService,
  ProjectExpirationOptionService,
  ShipmentStatusOptionService,
  ShipmentFilterOptionService,
  ApplianceFilterOptionService,
  WarehouseFilterOptionService,
  DeviceTypeFilterOptionService,
  DeviceOccupiedOptionService,
  WarehouseAppliancesOptionService,
  ApplianceStatusOptionService,
  ProjectSubprojectOptionService,
  ActionTypeFilterOptionService
} from '#service/autocomplete-service/service';
import { FilterOption, deviceLogFilterServiceOptions } from '#service/autocomplete-service/option';
import {
  SEARCH,
  DEVICE,
  DEVICE_MAP,
  DATE_RAGE_START,
  DATE_RANGE_END
} from './constants';
import {
  shipmentEventType,
  projectEventType,
  warehouseEventType,
  userEventType
} from '#type';

const optionServiceConfigByRelatedTo = {
  'shipment-log': { eventTypes: shipmentEventType },
  'project-log': { eventTypes: projectEventType },
  'warehouse-log': { eventTypes: warehouseEventType },
  'user-log': { eventTypes: userEventType },
  'device-log': deviceLogFilterServiceOptions
};

const servicesMap = {
  user: UserFilterOptionService,
  targetUser: UserFilterOptionService,
  device: DevicesFilterOptionService,
  project: ProjectsFilterOptionService,
  unexpired: ProjectExpirationOptionService,
  subproject: ProjectSubprojectOptionService,
  shipmentStatus: ShipmentStatusOptionService,
  shipment: ShipmentFilterOptionService,
  appliance: ApplianceFilterOptionService,
  applianceStatus: ApplianceStatusOptionService,
  warehouse: WarehouseFilterOptionService,
  deviceType: DeviceTypeFilterOptionService,
  occupied: DeviceOccupiedOptionService,
  whAppliance: WarehouseAppliancesOptionService,
  event: ActionTypeFilterOptionService
};

const getOptionServiceConfig = (name, relatedTo) => {
  if (name !== 'event') {
    return undefined;
  }

  return optionServiceConfigByRelatedTo[relatedTo];
};

const fetchOptions = async (name, ids, relatedTo) => {
  const searchField = 'label';
  const OptionsService = servicesMap[name];

  if (!OptionsService) return undefined;

  const optionServiceConfig = getOptionServiceConfig(name, relatedTo);

  if (name === 'event' && !optionServiceConfig) {
    return undefined;
  }

  const service = new OptionsService([searchField], null, optionServiceConfig);

  return service.getOptions(null, {}, ids);
};

export const getRelatedTo = () => {
  const pathName = window.location.pathname?.replace('/', '');

  // Case for device dashboard and map
  // should be used one filter path for both.
  if (pathName === DEVICE_MAP) {
    return DEVICE;
  }

  return pathName;
};

export const skipFetchingParamData = key => {
  const ignoredKeys = [SEARCH, DATE_RAGE_START, DATE_RANGE_END];
  return ignoredKeys.includes(key);
};

export const getFilterDataFromURL = async () => {
  const searchParams = new URLSearchParams(window.location.search);
  const existingParams = Object.fromEntries(searchParams.entries());
  const filterParams = Object.keys(existingParams);

  const getData = async () => {
    const filterResult = {};
    const relatedTo = getRelatedTo();
    for await (const param of filterParams) {
      try {
        if (!filterResult[relatedTo]) {
          filterResult[relatedTo] = {};
        }
        if (!filterResult[relatedTo][param]) {
          filterResult[relatedTo][param] = {};
        }

        let selectedOptions;
        if (skipFetchingParamData(param)) {
          selectedOptions = existingParams[param];
        } else {
          selectedOptions = await fetchOptions(param, existingParams[param], relatedTo);
        }

        filterResult[relatedTo][param].value = selectedOptions;
      } catch (error) {
        console.error(error);
      }
    }

    return filterResult;
  };

  if (!filterParams.length) return undefined;

  const filterData = await getData();

  return filterData;
};

export const isEmptyObject = (o = {}) => o && !Object.keys(o).length;

export const isFilterExist = (filter, excludeParams = []) => Object.keys(filter ?? {}).some(k => {
  if (excludeParams.includes(k)) {
    return false;
  }

  // skip default value
  if (filter[k]?.value?.length && k !== SEARCH) {
    const defaultValue = filter[k]?.value?.find(d => d?.data?.default);

    if (defaultValue) {
      return false;
    }
  }

  return filter[k]?.value?.length;
});

export const transformOptions = options => options?.warehouses?.map(option => {
  const label = `${option.label}`;
  return new FilterOption(option, label);
});

// Omit extra filter data and get only entities
export const getFilter = (filter = {}, omittedValues = []) => {
  const { initial, status, pristine, ...rest } = filter;

  if (omittedValues.length) {
    return Object.fromEntries(
      Object.entries(rest).filter(([key]) => !omittedValues.includes(key))
    );
  }

  return rest;
};
